import React, { FC, useEffect, useId, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { IDefaultInputProps } from "@/types/DefaultInputProps";
import { TextAreaWithRef } from "@/components/forms/inputsWithRef/textAreaWithRef";
import { RadioButtonsWithRef } from "@/components/forms/inputsWithRef/radioButtonsWithRef";
import { DateFieldWithRef } from "@/components/forms/inputsWithRef/dateFieldWithRef";
import { InputWithRef } from "@/components/forms/inputsWithRef/inputWithRef";
import { DropdownWithRef } from "@/components/forms/inputsWithRef/dropdownWithRef";
import { useTranslation } from "react-i18next";
import { ErrorMessage } from "@hookform/error-message";
import CustomErrorMessage from "@/components/forms/inputsWithRef/customErrorMessage";
import FindingFormVisibilityWrapper from "@/components/forms/findingFormVisibilityWrapper";
import { Badge, Grid, Paper } from "@mui/material";
import { api } from "@/helper/api";
import Modal from "@/components/modal";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import styles from "@/components/forms/defaultForm.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import ImageIcon from "@mui/icons-material/Image";
import EditNoteIcon from "@mui/icons-material/EditNote";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { styled } from "@mui/material/styles";
import NoteImageModal from "@/components/measurement/additionalMeasurementUploadsModal";
import type { FileUploadRequestOptions } from "@/helper/customHooks/useFileUploadProgress";
import classNames from "classnames";
import { useOnlineStatus } from "@/context/onlineStatusContextProvider";
import { useRegisterUnsavedChanges } from "@/context/unsavedChangesContext";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import useFormSubmission from "@/helper/customHooks/useFormSubmission";
import UnsavedChangesLeaveGuard from "@/components/forms/unsavedChangesLeaveGuard";

const normalizeFormValue = (value: unknown) =>
  value === null ||
  value === undefined ||
  (Array.isArray(value) && value.length === 0)
    ? ""
    : value;

const formValuesEqualIgnoringEmpty = (
  a: Record<string, unknown>,
  b: Record<string, unknown>
) => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (
      JSON.stringify(normalizeFormValue(a[key])) !==
      JSON.stringify(normalizeFormValue(b[key]))
    ) {
      return false;
    }
  }
  return true;
};

interface Props {
  onSubmit?: (data: any) => void;
  onEdit?: (data: any) => void;
  visibilityHandler?: (data: any) => void;
  input: IDefaultInputProps[] | any;
  entity: any;
  entityId?: any;
  visibility?: any;
  onSuccessHandler?: () => void;
  allowAdditionalMeasurementUploads?: boolean;
  formImageSet?: any;
  formNoteItem?: any;
  onAdditionalUploadSuccess?: () => void;

  //-------------------------------
  dataCallback?: any;
  preDefinedData?: any;
  preSubmitHandler?: any;
  onCancelFormHandler?: () => void;
  preSubmitConfirmMessage?: string;
  submitButtonText?: any;
  cancelButtonText?: any;
  cancelButton: boolean;
  findingId?: any;
  additionalUrlParameters?: any;
  targetEntity?: any; //for PUT & POST request - might differ from props.entity for GET
  targetEntityId?: any; //for PUT & POST request - might differ from props.entityId for GET
  withPaper?: boolean;
  isEditAllowed?: any;
  onCloseModal?: () => void;
  statusOfAppointment?: string;
  fieldsScrollWithinForm?: boolean;
  pageLayout?: boolean;
  guardUnsavedChanges?: boolean;
}

const DefaultForm: FC<Props> = (props: Props) => {
  const [errorModalOpen] = useState<boolean>(false);
  // const [errorOccured, setErrorOccured] = useState<boolean>(false);
  const [data, setData] = useState<any>([]);
  const { handleError, errorModal } = useErrorModal();
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const [
    additionalMeasurementUploadsModalOpen,
    setAdditionalMeasurementUploadsModalOpen,
  ] = useState<boolean>(false);
  const [
    additionalMeasurementUploadsModalType,
    setadditionalMeasurementUploadsModalType,
  ] = useState<"note" | "images" | null>(null);
  const [modalFieldName, setModalFieldName] = useState<string>("");

  const {
    register,
    handleSubmit,
    setValue,
    control,
    reset,
    getValues,
    watch,
    formState: { errors, isDirty },
  } = useForm<any>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    shouldFocusError: false,
  });
  const orderedInputs = [...props.input].sort((a, b) => a.order - b.order);
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField({
      fieldOrder: orderedInputs.map((input) => input.name),
      focusableSelector:
        'input:not([type="hidden"]), textarea, select, [role="combobox"]',
    });
  const { t } = useTranslation();
  const { showConfirmationModalAsync } = useConfirmationModal();
  const isOnline = useOnlineStatus();
  const unsavedChangesSourceId = useId();

  useRegisterUnsavedChanges(`DefaultForm-${unsavedChangesSourceId}`, isDirty);
  const baselineValuesRef = useRef<Record<string, unknown> | null>(null);
  const [hasGuardedUnsavedChanges, setHasGuardedUnsavedChanges] =
    useState<boolean>(false);

  const adoptCurrentValuesAsCleanBaseline = () => {
    if (!props.guardUnsavedChanges) return;
    reset(getValues(), { keepValues: true });
    baselineValuesRef.current = getValues();
    setHasGuardedUnsavedChanges(false);
  };

  useEffect(() => {
    if (!props.guardUnsavedChanges) return;
    const subscription = watch((values) => {
      if (!baselineValuesRef.current) return;
      setHasGuardedUnsavedChanges(
        !formValuesEqualIgnoringEmpty(values, baselineValuesRef.current)
      );
    });
    return () => subscription.unsubscribe();
  }, [watch, props.guardUnsavedChanges]);

  const resetStateOnCloseNotification = () => {
    setShowNotification(false);
    setNotificationMessage("");
  };

  const handleModal = (status: any, type: any, fieldName: string) => {
    setadditionalMeasurementUploadsModalType(type);
    setAdditionalMeasurementUploadsModalOpen(status);
    setModalFieldName(fieldName);
  };

  useEffect(() => {
    if (props.preDefinedData) {
      setData(props.preDefinedData);
    } else {
      api.genericApiRequest({
        method: "get",
        entity: props.entity,
        entityId: props.entityId,
        parametersToRender: {
          depth: "0",
          additionalUrlParameters: props.additionalUrlParameters,
        },
        successHandler: (res: any) => {
          if (props.dataCallback) {
            if (res.data.results) {
              props.dataCallback(res.data.results);
            } else {
              props.dataCallback(res.data);
            }
          }
          if (res.data.results) {
            setData(res.data.results);
          } else {
            setData(res.data);
          }
        },
        failHandler: (error: any) => {
          handleError(error);
        },
      });
    }
  }, [props.entity]);

  const input: any = [];

  for (const key in orderedInputs) {
    const inputProps = orderedInputs[key];

    const type = inputProps.type;
    const name = inputProps.name;
    const preDefinedValues = inputProps.preDefinedValues;

    //setting values if  and predefinedValues are present
    useEffect(() => {
      if (data) {
        setValue(name, data[name]);
        if (props.entity === "measurementDefinition") {
          if (data.reuse !== undefined) {
            if (data.reuse) {
              setValue("reuse", "yes");
            } else setValue("reuse", "no");
          }
          if (data.is_gbu !== undefined) {
            if (data.is_gbu) {
              setValue("is_gbu", "yes");
            } else setValue("is_gbu", "no");
          }
        }
      }
      if (preDefinedValues) {
        setValue(name, preDefinedValues);
      }

      if (props.isEditAllowed) {
        props.isEditAllowed(data);
      }
      if (data.unit && (data.unit === 0 || data.unit === "0")) {
        setValue("unit", "");
      }
    }, [data]);
    if (
      type !== "custom" &&
      props.visibility &&
      name in props.visibility &&
      !props.visibility[name]
    ) {
      continue; // Registering here would validate inputs that the visibility wrapper never mounts.
    }
    let element = <></>;

    switch (type) {
      case "text":
        element = (
          <Wrapper
            className={classNames(styles.defaultWrapper, {
              [styles.defaultWrapper_allowUploads]:
                props.allowAdditionalMeasurementUploads,
            })}
            key={inputProps.name}
            visibility={props.visibility}
            fieldname={name}
            validationTargetRef={registerValidationTarget(name)}
          >
            <TextAreaWithRef
              {...register(inputProps.name, {
                pattern: {
                  value: inputProps.pattern,
                  message: inputProps.errorMessageValidation,
                },
                onChange: inputProps.onChange,
                onBlur: inputProps.onBlur,
                required: {
                  value: inputProps.required,
                  message: inputProps.errorMessageRequired,
                },
              })}
              label={inputProps.label}
              labelRightSlot={
                props.allowAdditionalMeasurementUploads ? (
                  <AdditionalItemOperations
                    possibleRemedy={inputProps.possibleRemedy}
                    isGbu={inputProps.isGbu}
                    note={props?.formNoteItem?.[name]}
                    imageSet={props?.formImageSet?.[name]}
                    handleModal={handleModal}
                    fieldName={name}
                    statusOfAppointment={props.statusOfAppointment}
                  />
                ) : null
              }
              type={inputProps.type}
              cols={inputProps.cols}
              rows={inputProps.rows}
              defaultValue={inputProps.defaultValue}
              mandatory={inputProps.mandatory}
              name={inputProps.name}
              tooltip={inputProps.tooltip}
              tooltipMessage={inputProps.tooltipMessage}
              placement={inputProps.placement}
              required={inputProps.required}
              readonly={inputProps.readOnly}
            />
            <ErrorMessage
              errors={errors}
              name={inputProps.name}
              render={({ message }) => (
                <CustomErrorMessage
                  error={inputProps.error}
                  errorMessage={message}
                />
              )}
            />
          </Wrapper>
        );
        break;
      case "choice":
        element = (
          <Wrapper
            className={classNames(styles.defaultWrapper, {
              [styles.defaultWrapper_allowUploads]:
                props.allowAdditionalMeasurementUploads,
            })}
            key={name}
            visibility={props.visibility}
            fieldname={name}
            validationTargetRef={registerValidationTarget(name)}
          >
            <DropdownWithRef
              key={inputProps.name + inputProps.label}
              name={inputProps.name}
              options={inputProps.options}
              control={control}
              label={inputProps.label}
              labelRightSlot={
                props.allowAdditionalMeasurementUploads ? (
                  <AdditionalItemOperations
                    possibleRemedy={inputProps.possibleRemedy}
                    isGbu={inputProps.isGbu}
                    note={props?.formNoteItem?.[name]}
                    imageSet={props?.formImageSet?.[name]}
                    handleModal={handleModal}
                    fieldName={name}
                    statusOfAppointment={props.statusOfAppointment}
                  />
                ) : null
              }
              disabled={inputProps.disabled}
              isSearchable={inputProps.isSearchable}
              isClearable={inputProps.isClearable}
              required={inputProps.required}
              onBlur={inputProps.onBlur}
              onChange={inputProps.onDropdownChange}
              id={inputProps.name}
              error={inputProps.error}
              errorMessage={inputProps.errorMessageRequired}
              isMulti={false}
              tooltip={inputProps.tooltip}
              tooltipMessage={inputProps.tooltipMessage}
              placement={inputProps.placement}
              readOnly={inputProps.readOnly}
            />
          </Wrapper>
        );
        break;
      case "multiselect":
        element = (
          <Wrapper
            className={classNames(styles.defaultWrapper, {
              [styles.defaultWrapper_allowUploads]:
                props.allowAdditionalMeasurementUploads,
            })}
            key={name}
            visibility={props.visibility}
            fieldname={name}
            validationTargetRef={registerValidationTarget(name)}
          >
            <DropdownWithRef
              key={inputProps.name}
              name={inputProps.name}
              options={inputProps.options}
              control={control}
              label={inputProps.label}
              labelRightSlot={
                props.allowAdditionalMeasurementUploads ? (
                  <AdditionalItemOperations
                    possibleRemedy={inputProps.possibleRemedy}
                    isGbu={inputProps.isGbu}
                    note={props?.formNoteItem?.[name]}
                    imageSet={props?.formImageSet?.[name]}
                    handleModal={handleModal}
                    fieldName={name}
                    statusOfAppointment={props.statusOfAppointment}
                  />
                ) : null
              }
              isMulti={true}
              disabled={inputProps.disabled}
              isSearchable={inputProps.isSearchable}
              isClearable={inputProps.isClearable}
              required={inputProps.required}
              onBlur={inputProps.onBlur}
              onChange={inputProps.onDropdownChange}
              id={inputProps.name}
              error={inputProps.error}
              errorMessage={inputProps.errorMessageRequired}
              tooltip={inputProps.tooltip}
              tooltipMessage={inputProps.tooltipMessage}
              placement={inputProps.placement}
              readOnly={inputProps.readOnly}
            />
          </Wrapper>
        );
        break;
      case "bool":
        element = (
          <Wrapper
            className={classNames(styles.defaultWrapper, {
              [styles.defaultWrapper_allowUploads]:
                props.allowAdditionalMeasurementUploads,
            })}
            key={inputProps.name}
            visibility={props.visibility}
            fieldname={name}
            validationTargetRef={registerValidationTarget(name)}
          >
            <RadioButtonsWithRef
              {...register(inputProps.name, {
                pattern: {
                  value: inputProps.pattern,
                  message: inputProps.errorMessageValidation,
                },
                onChange: inputProps.onChange,
                onBlur: inputProps.onBlur,
                required: {
                  value: inputProps.required,
                  message: inputProps.errorMessageRequired,
                },
              })}
              name={inputProps.name}
              readonly={inputProps.readOnly}
              choices={inputProps.choices}
              defaultValue={inputProps.defaultValue}
              label={inputProps.label}
              labelRightSlot={
                props.allowAdditionalMeasurementUploads ? (
                  <AdditionalItemOperations
                    possibleRemedy={inputProps.possibleRemedy}
                    isGbu={inputProps.isGbu}
                    note={props?.formNoteItem?.[name]}
                    imageSet={props?.formImageSet?.[name]}
                    handleModal={handleModal}
                    fieldName={name}
                    statusOfAppointment={props.statusOfAppointment}
                  />
                ) : null
              }
              tooltip={inputProps.tooltip}
              tooltipMessage={inputProps.tooltipMessage}
              placement={inputProps.placement}
              required={inputProps.required}
            />
            <ErrorMessage
              errors={errors}
              name={inputProps.name}
              render={({ message }) => (
                <CustomErrorMessage
                  error={inputProps.error}
                  errorMessage={message}
                />
              )}
            />
          </Wrapper>
        );
        break;
      case "boolNoStatement":
        element = (
          <Wrapper
            className={classNames(styles.defaultWrapper, {
              [styles.defaultWrapper_allowUploads]:
                props.allowAdditionalMeasurementUploads,
            })}
            key={inputProps.name}
            visibility={props.visibility}
            fieldname={name}
            validationTargetRef={registerValidationTarget(name)}
          >
            <RadioButtonsWithRef
              {...register(inputProps.name, {
                pattern: {
                  value: inputProps.pattern,
                  message: inputProps.errorMessageValidation,
                },
                onChange: inputProps.onChange,
                onBlur: inputProps.onBlur,
                required: {
                  value: inputProps.required,
                  message: inputProps.errorMessageRequired,
                },
              })}
              name={inputProps.name}
              label={inputProps.label}
              labelRightSlot={
                props.allowAdditionalMeasurementUploads ? (
                  <AdditionalItemOperations
                    possibleRemedy={inputProps.possibleRemedy}
                    isGbu={inputProps.isGbu}
                    note={props?.formNoteItem?.[name]}
                    imageSet={props?.formImageSet?.[name]}
                    handleModal={handleModal}
                    fieldName={name}
                    statusOfAppointment={props.statusOfAppointment}
                  />
                ) : null
              }
              readonly={inputProps.readOnly}
              boolOption
              choices={inputProps.choices}
              defaultValue={inputProps.defaultValue}
              tooltip={inputProps.tooltip}
              tooltipMessage={inputProps.tooltipMessage}
              placement={inputProps.placement}
              required={inputProps.required}
            />
            <ErrorMessage
              errors={errors}
              name={inputProps.name}
              render={({ message }) => (
                <CustomErrorMessage
                  error={inputProps.error}
                  errorMessage={message}
                />
              )}
            />
          </Wrapper>
        );
        break;
      case "date":
        element = (
          <Wrapper
            className={classNames(styles.defaultWrapper, {
              [styles.defaultWrapper_allowUploads]:
                props.allowAdditionalMeasurementUploads,
            })}
            key={inputProps.name}
            visibility={props.visibility}
            fieldname={name}
            validationTargetRef={registerValidationTarget(name)}
          >
            <Controller
              name={inputProps.name}
              control={control}
              rules={{
                pattern: {
                  value: /^(19|20)\d\d-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/,
                  message: inputProps.errorMessageValidation,
                },
                required: {
                  value: inputProps.required,
                  message: inputProps.errorMessageRequired,
                },
              }}
              render={({ field: { value, onChange, onBlur, ref } }) => (
                <DateFieldWithRef
                  ref={ref}
                  value={value}
                  onChange={(event) => {
                    onChange(event);
                    inputProps.onChange?.(event);
                  }}
                  onBlur={(event) => {
                    onBlur();
                    inputProps.onBlur?.(event);
                  }}
                  label={inputProps.label}
                  name={inputProps.name}
                  readonly={inputProps.readOnly}
                  tooltip={inputProps.tooltip}
                  tooltipMessage={inputProps.tooltipMessage}
                  placement={inputProps.placement}
                  required={inputProps.required}
                />
              )}
            />
            <ErrorMessage
              errors={errors}
              name={inputProps.name}
              render={({ message }) => (
                <CustomErrorMessage
                  error={inputProps.error}
                  errorMessage={message}
                />
              )}
            />
          </Wrapper>
        );
        break;
      // handling of customComponent not through useForm
      case "custom":
        element = (
          <React.Fragment key={inputProps.name}>
            {inputProps.visible && (
              <Wrapper
                className={classNames(styles.defaultWrapper, {
                  [styles.defaultWrapper_allowUploads]:
                    props.allowAdditionalMeasurementUploads,
                })}
                fieldname={name}
                validationTargetRef={registerValidationTarget(name)}
                key={inputProps.name}
              >
                {inputProps.customElement}
              </Wrapper>
            )}
          </React.Fragment>
        );
        break;

      default:
        element = (
          <Wrapper
            className={classNames(styles.defaultWrapper, {
              [styles.defaultWrapper_allowUploads]:
                props.allowAdditionalMeasurementUploads,
            })}
            key={name + type}
            visibility={props.visibility}
            fieldname={name}
            validationTargetRef={registerValidationTarget(name)}
          >
            <InputWithRef
              {...register(inputProps.name, {
                pattern: {
                  value: inputProps.pattern,
                  message: inputProps.errorMessageValidation,
                },
                onChange: inputProps.onChange,
                onBlur: inputProps.onBlur,
                required: {
                  value: inputProps.required,
                  message: inputProps.errorMessageRequired,
                },
                ...(inputProps.type === "int" || inputProps.type === "float"
                  ? {
                      setValueAs: (value: any) => {
                        if (value === "") return null;
                        const number = Number(value);
                        return Number.isNaN(number) ? null : number;
                      },
                    }
                  : {}),

                validate:
                  inputProps.type === "int"
                    ? (value) => {
                        if (
                          !inputProps.required &&
                          (value === "" ||
                            value === undefined ||
                            value === null)
                        ) {
                          return true;
                        }
                        return /^\d+$/.test(value)
                          ? true
                          : t("validation_message_type_int");
                      }
                    : undefined,
              })}
              label={inputProps.label}
              onFocus={inputProps.onFocus}
              name={inputProps.name}
              autoComplete={inputProps.autoComplete}
              placeholder={inputProps.placeholder}
              tooltip={inputProps.tooltip}
              tooltipMessage={inputProps.tooltipMessage}
              placement={inputProps.placement}
              required={inputProps.required}
              type={inputProps.type}
              readonly={inputProps.readOnly}
              disabled={inputProps.disabled}
            />
            <ErrorMessage
              errors={errors}
              name={inputProps.name}
              render={({ message }) => (
                <CustomErrorMessage
                  error={inputProps.error}
                  errorMessage={message}
                />
              )}
            />
          </Wrapper>
        );
    }

    input.push(element);
  }

  useEffect(() => {
    adoptCurrentValuesAsCleanBaseline();
  }, [data]);

  const onSubmit = async (data: any) => {
    let result = data;

    if (props.preSubmitConfirmMessage) {
      const isConfirmed = await showConfirmationModalAsync(
        props.preSubmitConfirmMessage
      );
      if (!isConfirmed) {
        return;
      }
    }

    if (props.preSubmitHandler) {
      result = props.preSubmitHandler(data);
    }

    if (props.entity === "finding") {
      result = { form: props.entityId, values: data, finding: props.findingId };
    }

    if (props.onCloseModal) props.onCloseModal();

    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    try {
      if (props.entityId) {
        // eslint-disable-next-line
        await new Promise<any>((resolve, reject) => {
          try {
            api.genericApiRequest({
              method: "put",
              entityId: props.targetEntityId,
              entity: props.targetEntity,
              submitData: result,
              config: config,
              successHandler: (res: any) => {
                resolve(res);
              },
              failHandler: (error: any, status: any, code: any) => {
                if (error === undefined) {
                  if (code === "ERR_NETWORK") {
                    reject(t("submit_failed"));
                  } else reject(t("generic_error_message", { error: status }));
                } else reject(error);
              },
            });
          } catch (error) {
            reject(error);
          }
        });
        setNotificationMessage(t("save_successful"));
        setNotificationVariant("success");
        setShowNotification(true);
        adoptCurrentValuesAsCleanBaseline();
        props.onSuccessHandler?.();
      } else {
        // eslint-disable-next-line
        await new Promise<any>((resolve, reject) => {
          try {
            api.genericApiRequest({
              method: "post",
              entity: props.targetEntity,
              successHandler: (res: any) => {
                resolve(res);
              },
              failHandler: (error: any, status: any, code: any) => {
                if (error === undefined) {
                  if (code === "ERR_NETWORK") {
                    reject(t("submit_failed"));
                  } else reject(t("generic_error_message", { error: status }));
                } else reject(error);
                handleError(error);
              },
              submitData: result,
              config: config,
            });
          } catch (error) {
            reject(error);
          }
        });
        setNotificationMessage(t("create_successful"));
        setNotificationVariant("success");
        setShowNotification(true);
        adoptCurrentValuesAsCleanBaseline();
        props.onSuccessHandler?.();
      }
    } catch (error) {
      handleError(error);
    }
  };
  let submitButtonText = t("save");
  let cancelButtonText = t("cancel");

  if (props.submitButtonText) {
    submitButtonText = props.submitButtonText;
  }

  if (props.cancelButtonText) {
    cancelButtonText = props.cancelButtonText;
  }

  const disableSubmitButton = () => {
    if (props.entity === "anchorData") {
      if (!isDirty) {
        return true;
      }
    }
    return false;
  };

  const isSubmitDisabled = disableSubmitButton() || !isOnline;

  const handleManualFormSubmit = async (values: any) => {
    try {
      await onSubmit(values);
    } catch {
      //errorhandling is done in the onSubmit. Error not thrown again
    }
  };
  const { handleSubmit: submitForm, isSubmitting } = useFormSubmission({
    disabled: isSubmitDisabled,
    onSubmit: handleSubmit(handleManualFormSubmit, (errors) => {
      focusFirstInvalidField(errors);
    }),
  });
  const submitMeasurementNote = async (
    data: any,
    onError: any,
    measurement_name: string | undefined,
    onSuccess?: () => void
  ): Promise<any> => {
    const additionalRouteParts = {
      submit_note: "/",
    };
    const result = {
      payload: data,
      finding_id: props.findingId,
      measurement_name: measurement_name,
    };

    return new Promise((resolve, reject) => {
      try {
        api.genericApiRequest({
          entity: "measurement",
          method: "put",
          additionalRouteParts,
          submitData: result,
          successHandler: (response: any) => {
            resolve(response);
            setNotificationMessage(t("save_successful"));
            setNotificationVariant("success");
            setShowNotification(true);
            onSuccess?.();
            if (props.onAdditionalUploadSuccess) {
              props.onAdditionalUploadSuccess();
            }
          },
          failHandler: (error: any) => {
            reject(error);
          },
        });
      } catch (err) {
        onError(err);
        reject(err);
      }
    });
  };

  const handleDeleteMeasurementNote = async (note: any) => {
    const noteId = note.id;
    const confirmDelete = await showConfirmationModalAsync(
      t("delete_entry_question")
    );

    if (!confirmDelete) {
      return;
    }
    api.genericApiRequest({
      entity: "note",
      entityId: noteId,
      method: "delete",
      successHandler: () => {
        setNotificationMessage(t("delete_successful"));
        setNotificationVariant("success");
        setShowNotification(true);
        if (props.onAdditionalUploadSuccess) {
          props.onAdditionalUploadSuccess();
        }
        handleModal(false, "", "");
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };
  const handleDeleteFile = async (file: any) => {
    const confirmDelete = await showConfirmationModalAsync(
      t("delete_file_question")
    );
    if (!confirmDelete) {
      return;
    }

    api.genericApiRequest({
      entity: "file",
      method: "delete",
      entityId: String(file.id),
      successHandler: () => {
        setNotificationMessage(t("delete_successful"));
        setNotificationVariant("success");
        setShowNotification(true);
        if (props.onAdditionalUploadSuccess) {
          props.onAdditionalUploadSuccess();
        }
      },
      failHandler: (error: any) => handleError(error),
    });
  };

  const submitImages = async (
    files: File[],
    measurementName: any,
    { onUploadProgress }: FileUploadRequestOptions,
  ): Promise<any> => {
    const additionalRouteParts = {
      submit_files: "/",
    };

    const formData = new FormData();
    formData.append("finding_id", props.findingId);
    files.forEach((file: any) => {
      formData.append("file_set", file, `${measurementName}---${file.name}`);
    });

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    };

    return new Promise((resolve, reject) => {
      try {
        api.genericApiRequest({
          entity: "measurement",
          method: "PUT",
          additionalRouteParts,
          config,
          submitData: formData,
          disableGlobalSpinner: true,
          successHandler: (response: any) => {
            resolve(response);
            if (props.onAdditionalUploadSuccess) {
              props.onAdditionalUploadSuccess();
            }
          },
          failHandler: (error: any) => {
            reject(error);
          },
        });
      } catch (err) {
        handleError(err);
        reject(err);
      }
    });
  };

  const renderActionBar = (cancelButtonClassName?: string) => (
    <div className={classNames(styles.buttonWrapper, { [styles.stickyActionBar]: props.pageLayout })} data-testid="defaultForm-action-bar">
      <Grid container justifyContent={"space-between"}>
        <Grid item>
          {props.cancelButton && (
            <GenericButton
              className={cancelButtonClassName}
              variant="outlined"
              onClick={props.onCancelFormHandler}
            >
              {cancelButtonText}
            </GenericButton>
          )}
        </Grid>
        {hasGuardedUnsavedChanges && !props.pageLayout && (
          <Grid item className={styles.unsavedChangesHint}>
            {t("unsaved_changes_hint")}
          </Grid>
        )}
        <Grid item xs={3}>
          <GenericButton
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitDisabled}
            data-testid="defaultForm-submit-button"
            loading={isSubmitting}
            showSpinnerOnDisable={!isSubmitDisabled}
          >
            {submitButtonText}
          </GenericButton>
        </Grid>
      </Grid>
    </div>
  );

  return (
    <>
      {props.guardUnsavedChanges && (
        <UnsavedChangesLeaveGuard
          hasUnsavedChanges={hasGuardedUnsavedChanges}
        />
      )}
      {props.withPaper ? (
        <Paper
          className={classNames({
            [styles.fieldsScrollWithinForm]: props.fieldsScrollWithinForm && !props.pageLayout,
            [styles.pageLayout]: props.pageLayout,
          })}
          sx={{
            padding: "0.75rem",
            borderRadius: "0.75rem",
            maxWidth: props.pageLayout ? "100%" : "80%",
            // as a flex item the paper would shrink to its content, so the scrolling layout sets the width explicitly
            width: props.pageLayout ? "100%" : props.fieldsScrollWithinForm ? "80%" : undefined,
            margin: "0 auto",
            overflow: props.pageLayout ? "visible" : props.fieldsScrollWithinForm ? "hidden" : "scroll",
            // width: "80%",
          }}
        >
          <form noValidate onSubmit={submitForm}>
            <div
              className={classNames(styles.inputWrapper, {
                [styles.gap]: !props.allowAdditionalMeasurementUploads,
              })}
            >
              {input}
            </div>
            {renderActionBar()}
          </form>
        </Paper>
      ) : (
        <>
          <form
            noValidate
            onSubmit={submitForm}
            className={classNames({
              [styles.fieldsScrollWithinForm]: props.fieldsScrollWithinForm && !props.pageLayout,
              [styles.pageLayout]: props.pageLayout,
            })}
          >
            <div
              className={classNames(styles.inputWrapper, {
                [styles.gap]: !props.allowAdditionalMeasurementUploads,
              })}
            >
              {input}
            </div>
            {renderActionBar(styles.cancelButton)}
          </form>
        </>
      )}
      <Modal
        open={errorModalOpen}
        title={t("error_occurred")}
        hideSubmitButton={true}
        cancelButtonText={t("confirm")}
      >
        <CustomErrorMessage
          error={"error"}
          errorMessage={t("complete_required_fields")}
          errorFontSize={"1rem"}
        />
      </Modal>
      <NoteImageModal
        open={additionalMeasurementUploadsModalOpen}
        type={additionalMeasurementUploadsModalType}
        handleDeleteNote={handleDeleteMeasurementNote}
        statusOfAppointment={props.statusOfAppointment || ""}
        handleError={handleError}
        handleDeleteFile={handleDeleteFile}
        onClose={() => handleModal(false, "", "")}
        fieldName={modalFieldName}
        onSubmit={async (
          data: any,
          id: string,
          measurementName?: string,
          options?: FileUploadRequestOptions,
        ) => {
          if (additionalMeasurementUploadsModalType === "note") {
            return submitMeasurementNote(
              data,
              handleError,
              measurementName,
              props.onAdditionalUploadSuccess
            );
          } else {
            return submitImages(data, measurementName, options!);
          }
        }}
        noteItem={props.formNoteItem}
        imageSet={props.formImageSet}
      />

      <GenericNotification
        message={showNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
      {errorModal}
    </>
  );
};

type MeasurementNoteItem = any;
type MeasurementImageSet = any;

type FormNoteItem = Record<string, MeasurementNoteItem>;
type FormImageSet = Record<string, MeasurementImageSet[]>;

type WrapperProps = {
  validationTargetRef?: React.Ref<HTMLDivElement>;
  visibility?: boolean;
  fieldname: string;
  className?: string;
  children: React.ReactNode;
};

const Wrapper: React.FC<WrapperProps> = ({
  visibility,
  fieldname,
  className,
  children,
  validationTargetRef,
}) => {
  if (!visibility) {
    return (
      <div className={className} ref={validationTargetRef}>
        {children}
      </div>
    );
  } else {
    return (
      <>
        <FindingFormVisibilityWrapper
          visibility={visibility}
          fieldname={fieldname}
          className={className}
          validationTargetRef={validationTargetRef}
        >
          {children}
        </FindingFormVisibilityWrapper>
      </>
    );
  }
};

const CustomNoteBadge = styled(Badge)(() => ({
  "& .MuiBadge-badge": {
    color: "#FFF",
    backgroundColor: "#ff6428",
    lineHeight: "inherit",
  },
}));
const CustomImageBadge = styled(Badge)(() => ({
  "& .MuiBadge-badge": {
    color: "#FFF",
    backgroundColor: "#8c1ec8",
    lineHeight: "inherit",
  },
}));

type AdditionalItemOperationsProps = {
  handleModal: (
    open: boolean,
    type: "note" | "images" | null,
    fieldName: string
  ) => void;
  note?: FormNoteItem;
  imageSet?: FormImageSet;
  possibleRemedy?: string;
  isGbu?: boolean;
  fieldName: string;
  statusOfAppointment: any;
};
const AdditionalItemOperations: React.FC<AdditionalItemOperationsProps> = ({
  handleModal,
  note,
  imageSet,
  possibleRemedy,
  isGbu,
  fieldName,
  statusOfAppointment,
}) => {
  const [possibleRemedyModalOpen, setPossibleRemedyModalOpen] =
    useState<boolean>(false);
  const { t } = useTranslation();
  const statusArrayForDeleteAllowed = [
    "open",
    "in_progress",
    "waiting_for_lab_results",
    "evaluating_lab_results",
  ];

  const deleteAllowed =
    statusArrayForDeleteAllowed.includes(statusOfAppointment);

  const hasNote = Boolean(note?.note && Object.keys(note.note).length > 0);
  const imageCount = imageSet?.image_set?.length ?? 0;
  const hasImages = imageCount > 0;
  const hasPossibleRemedy =
    typeof possibleRemedy === "string" && possibleRemedy.trim() !== "";
  const showPossibleRemedyButton = Boolean(isGbu) && hasPossibleRemedy;

  return (
    <>
      <div className={styles.additionalItemActions}>
        {showPossibleRemedyButton && (
          <GenericButton
            variant="icon"
            color="iconBase"
            onClick={() => setPossibleRemedyModalOpen(true)}
          >
            <HelpOutlineIcon />
          </GenericButton>
        )}

        {(deleteAllowed || hasNote) && (
          <GenericButton
            variant="icon"
            color="iconBase"
            onClick={() => handleModal(true, "note", fieldName)}
          >
            <CustomNoteBadge badgeContent={hasNote ? "1" : null}>
              <EditNoteIcon />
            </CustomNoteBadge>
          </GenericButton>
        )}

        {(deleteAllowed || hasImages) && (
          <GenericButton
            variant="icon"
            color="iconBase"
            onClick={() => handleModal(true, "images", fieldName)}
          >
            <CustomImageBadge badgeContent={hasImages ? imageCount : null}>
              <ImageIcon />
            </CustomImageBadge>
          </GenericButton>
        )}
      </div>
      <Modal
        open={possibleRemedyModalOpen}
        title={t("possible_remedy")}
        hideCancelButton={true}
        submitButtonText={t("ok_capital")}
        buttonPlacement="flex-end"
        maxWidth="42rem"
        onClose={() => setPossibleRemedyModalOpen(false)}
        onSubmit={() => setPossibleRemedyModalOpen(false)}
      >
        {possibleRemedy}
      </Modal>
    </>
  );
};

export default DefaultForm;
